import { useEffect, useState, useRef } from "react";

/**
 * Alert Card Image Layout Algorithm
 *
 * This algorithm handles optimal layout for 1-4 images in a constrained container space.
 * It features several key optimizations:
 *
 * 1. Image Loading and Caching:
 *    - Loads images asynchronously with timeout and error handling
 *    - Caches image dimensions to avoid redundant loading when only container size changes
 *
 * 2. Image Pre-processing:
 *    - Sorts images by aspect ratio and size to ensure consistent layout regardless of input order
 *    - Filters out invalid images
 *
 * 3. Layout Strategies:
 *    - 1 image: Centered with maximum size while maintaining aspect ratio
 *    - 2 images: Chooses between horizontal/vertical layout based on which offers better space utilization
 *    - 3 images: Selects best layout from three options (A: 2 up + 1 down, B: vertical stack, C: horizontal row)
 *    - 4 images: Quadrant-based layout with optimized placement based on image characteristics
 *
 * 4. Global Optimization:
 *    - For 4-image layout, places images in quadrants based on visual importance and image properties
 *    - Adjusts vertical position to ensure proper visual balance
 */

interface AlertCardImageContainerProps {
  images: string[];
  containerWidth: number;
  containerHeight: number;
}

interface ImageDimension {
  src: string;
  w: number;
  h: number;
}

export const AlertCardImageContainer = ({
  images,
  containerWidth,
  containerHeight,
}: AlertCardImageContainerProps) => {
  const [styles, setStyles] = useState<
    { src: string; width: number; height: number; top: number; left: number }[]
  >([]);

  // Use ref to cache loaded image dimensions
  const loadedImagesRef = useRef<Map<string, ImageDimension>>(new Map());

  // Load images when the images array changes
  useEffect(() => {
    if (!images.length) {
      setStyles([]);
      return;
    }

    // Find images that haven't been loaded yet
    const unloadedImages = images.filter(
      (src) => !loadedImagesRef.current.has(src),
    );

    if (unloadedImages.length === 0) {
      // All images already loaded, calculate layout directly
      calculateLayout();
      return;
    }

    // Load new images
    Promise.all(
      unloadedImages.map(
        (src) =>
          new Promise<ImageDimension>((resolve) => {
            const img = new Image();
            img.src = src;
            img.onload = () => {
              const dimension = {
                src,
                w: img.naturalWidth,
                h: img.naturalHeight,
              };
              // Cache image dimensions
              loadedImagesRef.current.set(src, dimension);
              resolve(dimension);
            };
            img.onerror = () => {
              console.warn(`Failed to load image: ${src}`);
              const dimension = { src, w: 100, h: 100 };
              loadedImagesRef.current.set(src, dimension);
              resolve(dimension);
            };
            // Add timeout handling
            setTimeout(() => {
              if (!img.complete) {
                console.warn(`Image loading timeout: ${src}`);
                const dimension = { src, w: 100, h: 100 };
                loadedImagesRef.current.set(src, dimension);
                resolve(dimension);
              }
            }, 10000); // 10 second timeout
          }),
      ),
    ).then(() => {
      // Calculate layout after all images are loaded
      calculateLayout();
    });
  }, [images]);

  // Recalculate layout when image array or container size changes
  useEffect(() => {
    if (images.length > 0) {
      calculateLayout();
    }
  }, [containerWidth, containerHeight, images]);

  // Layout calculation function
  const calculateLayout = () => {
    // Get image dimensions from cache for current images
    const imageDimensions = images
      .map((src) => loadedImagesRef.current.get(src))
      .filter((dim): dim is ImageDimension => dim !== undefined);

    if (imageDimensions.length === 0) {
      setStyles([]);
      return;
    }

    // Sort images to ensure layout consistency
    const sortedImageDimensions = sortImagesByAttributes(imageDimensions);

    const n = sortedImageDimensions.length;

    // --- 1 image: Centered ---
    if (n === 1) {
      const { src, w, h } = sortedImageDimensions[0];
      const scale = Math.min(containerWidth / w, containerHeight / h);
      const dispW = w * scale;
      const dispH = h * scale;
      setStyles([
        {
          src,
          width: dispW,
          height: dispH,
          left: (containerWidth - dispW) / 2,
          top: (containerHeight - dispH) / 2,
        },
      ]);
      return;
    }

    // --- 2 images: Compare horizontal vs vertical layout and align to center line ---
    if (n === 2) {
      const [img1, img2] = sortedImageDimensions;

      // Minimum scale for horizontal layout, each image in half width
      const scale1_h = Math.min(
        containerWidth / 2 / img1.w,
        containerHeight / img1.h,
      );
      const scale2_h = Math.min(
        containerWidth / 2 / img2.w,
        containerHeight / img2.h,
      );
      const minScaleH = Math.min(scale1_h, scale2_h);

      // Minimum scale for vertical layout, each image in half height
      const scale1_v = Math.min(
        containerWidth / img1.w,
        containerHeight / 2 / img1.h,
      );
      const scale2_v = Math.min(
        containerWidth / img2.w,
        containerHeight / 2 / img2.h,
      );
      const minScaleV = Math.min(scale1_v, scale2_v);

      // Choose layout with less whitespace (larger scale)
      const horizontal = minScaleH >= minScaleV;

      const styles2 = sortedImageDimensions.map(({ src, w, h }, idx) => {
        let scale: number, dispW: number, dispH: number;
        let left: number, top: number;

        if (horizontal) {
          const halfW = containerWidth / 2;
          scale = Math.min(halfW / w, containerHeight / h);
          dispW = w * scale;
          dispH = h * scale;

          // First image align right to center; second image align left to center
          left = idx === 0 ? halfW - dispW : halfW;
          top = (containerHeight - dispH) / 2;
        } else {
          const halfH = containerHeight / 2;
          scale = Math.min(containerWidth / w, halfH / h);
          dispW = w * scale;
          dispH = h * scale;

          left = (containerWidth - dispW) / 2;
          // First image bottom edge to center; second image top edge to center
          top = idx === 0 ? halfH - dispH : halfH;
        }

        return { src, width: dispW, height: dispH, left, top };
      });

      setStyles(styles2);
      return;
    }

    // --- 3 images: Choose best layout from three options (A: 2 up + 1 down; B: vertical rows; C: horizontal columns) ---
    if (n === 3) {
      type ThreeImageLayout = {
        cellW: (idx: number) => number;
        cellH: (idx: number) => number;
        left: (dispW: number, dispH: number, idx: number) => number;
        top: (dispW: number, dispH: number, idx: number) => number;
      };

      const CW = containerWidth;
      const CH = containerHeight;

      // Layout A: Two images on top, one on bottom
      const layoutA: ThreeImageLayout = {
        // Top two each takes half width, bottom one takes full width
        cellW: (idx) => (idx < 2 ? CW / 2 : CW),
        // Each row takes half height
        cellH: () => CH / 2,

        // Left margins: top two centered at 0 and CW/2, bottom centered
        left: (w, h, idx) =>
          idx < 2 ? idx * (CW / 2) + (CW / 2 - w) / 2 : (CW - w) / 2,

        // Top margins: top two centered in top half, bottom centered in bottom half
        top: (w, h, idx) =>
          idx < 2 ? (CH / 2 - h) / 2 : CH / 2 + (CH / 2 - h) / 2,
      };

      // Layout B: Vertical three rows
      const layoutB: ThreeImageLayout = {
        cellW: () => CW,
        cellH: () => CH / 3,
        left: (w) => (CW - w) / 2,
        top: (w, h, idx) => idx * (CH / 3) + (CH / 3 - h) / 2,
      };

      // Layout C: Horizontal three columns
      const layoutC: ThreeImageLayout = {
        cellW: () => CW / 3,
        cellH: () => CH,
        left: (w, h, idx) => idx * (CW / 3) + (CW / 3 - w) / 2,
        top: (w, h) => (CH - h) / 2,
      };

      const layouts = { A: layoutA, B: layoutB, C: layoutC };

      // Score each layout based on minimum scale of all three images
      const scored = (
        Object.entries(layouts) as [keyof typeof layouts, ThreeImageLayout][]
      ).map(([key, L]) => {
        const minScale = Math.min(
          ...sortedImageDimensions.map(({ w, h }, idx) =>
            Math.min(L.cellW(idx) / w, L.cellH(idx) / h),
          ),
        );
        return [key, minScale] as const;
      });
      // Select layout with highest score
      const bestKey = scored.reduce((a, b) =>
        a[1] > b[1] ? a : b,
      )[0] as keyof typeof layouts;
      const best = layouts[bestKey];

      const styled = sortedImageDimensions.map(({ src, w, h }, idx) => {
        const scale = Math.min(best.cellW(idx) / w, best.cellH(idx) / h);
        const dispW = w * scale,
          dispH = h * scale;
        return {
          src,
          width: dispW,
          height: dispH,
          left: best.left(dispW, dispH, idx),
          top: best.top(dispW, dispH, idx),
        };
      });

      // Calculate vertical offset to position top image at 10% of container height
      const minTop = Math.min(...styled.map((s) => s.top));
      const desiredTop = containerHeight * 0.1;
      const offset = Math.max(minTop - desiredTop, 0); // Only move up, not down

      setStyles(styled.map((s) => ({ ...s, top: s.top - offset })));
      return;
    }

    // --- 4 images: Quadrant-based layout with minimized whitespace ---
    if (n === 4) {
      // Use layout that minimizes whitespace
      const layoutStyles = findOptimalFourImageLayout(
        sortedImageDimensions,
        containerWidth,
        containerHeight,
      );

      const minTop = Math.min(...layoutStyles.map((s) => s.top));
      const desiredTop = containerHeight * 0.1;
      const offset = Math.max(minTop - desiredTop, 0);

      setStyles(layoutStyles.map((s) => ({ ...s, top: s.top - offset })));
      return;
    }
  };

  // Sort images by attributes to ensure layout consistency
  const sortImagesByAttributes = (images: ImageDimension[]) => {
    // Create a copy to avoid modifying the original array
    const result = [...images];

    // Sort by aspect ratio and image size
    result.sort((a, b) => {
      // First sort by aspect ratio (width/height)
      const ratioA = a.w / a.h;
      const ratioB = b.w / b.h;

      // When aspect ratios differ significantly, sort by ratio
      if (Math.abs(ratioA - ratioB) > 0.2) {
        return ratioA - ratioB; // From portrait to landscape
      }

      // When aspect ratios are similar, sort by area
      const areaA = a.w * a.h;
      const areaB = b.w * b.h;
      return areaB - areaA; // From large to small
    });

    return result;
  };

  // Find optimal layout for 4 images that minimizes whitespace
  const findOptimalFourImageLayout = (
    images: ImageDimension[],
    containerWidth: number,
    containerHeight: number,
  ): {
    src: string;
    width: number;
    height: number;
    top: number;
    left: number;
  }[] => {
    interface ImageStyle {
      src: string;
      width: number;
      height: number;
      top: number;
      left: number;
    }

    interface FourImageLayout {
      id: string;
      calcPositions: (images: ImageDimension[]) => ImageStyle[];
      calcWhitespace: (
        positions: { width: number; height: number }[],
      ) => number;
    }

    const CW = containerWidth;
    const CH = containerHeight;
    const cx = CW / 2;
    const cy = CH / 2;

    // Layout A: 2×2 grid (quadrant-based)
    const layoutA: FourImageLayout = {
      id: "grid",
      calcPositions: (imgs) => {
        return imgs.map(({ src, w, h }, idx) => {
          const quadrant = idx % 4;
          const offsetX = quadrant === 0 || quadrant === 2 ? -1 : 0;
          const offsetY = quadrant === 0 || quadrant === 1 ? -1 : 0;

          const slotW = cx;
          const slotH = cy;
          const scale = Math.min(slotW / w, slotH / h);
          const dispW = w * scale;
          const dispH = h * scale;

          const left = offsetX < 0 ? cx - dispW : cx;
          const top = offsetY < 0 ? cy - dispH : cy;

          return { src, width: dispW, height: dispH, left, top };
        });
      },
      calcWhitespace: (positions) => {
        // Calculate unused whitespace in each quadrant
        let totalArea = 0;
        positions.forEach(({ width, height }) => {
          totalArea += width * height;
        });
        return CW * CH - totalArea;
      },
    };

    // Layout B: Top row has one large image, bottom row has three smaller images
    const layoutB: FourImageLayout = {
      id: "1-over-3",
      calcPositions: (imgs) => {
        const result: ImageStyle[] = [];

        // First image takes top half
        const img0 = imgs[0];
        const scale0 = Math.min(CW / img0.w, CH / 2 / img0.h);
        const width0 = img0.w * scale0;
        const height0 = img0.h * scale0;
        result.push({
          src: img0.src,
          width: width0,
          height: height0,
          left: (CW - width0) / 2,
          top: CH / 4 - height0 / 2,
        });

        // Bottom three images each get 1/3 of the bottom half
        for (let i = 1; i < 4; i++) {
          const img = imgs[i];
          const slotW = CW / 3;
          const slotH = CH / 2;
          const scale = Math.min(slotW / img.w, slotH / img.h);
          const width = img.w * scale;
          const height = img.h * scale;
          result.push({
            src: img.src,
            width,
            height,
            left: (i - 1) * slotW + (slotW - width) / 2,
            top: CH / 2 + (slotH - height) / 2,
          });
        }

        return result;
      },
      calcWhitespace: (positions) => {
        let totalArea = 0;
        positions.forEach(({ width, height }) => {
          totalArea += width * height;
        });
        return CW * CH - totalArea;
      },
    };

    // Layout C: Left column has one large image, right column has three smaller images
    const layoutC: FourImageLayout = {
      id: "1-beside-3",
      calcPositions: (imgs) => {
        const result: ImageStyle[] = [];

        // First image takes left half
        const img0 = imgs[0];
        const scale0 = Math.min(CW / 2 / img0.w, CH / img0.h);
        const width0 = img0.w * scale0;
        const height0 = img0.h * scale0;
        result.push({
          src: img0.src,
          width: width0,
          height: height0,
          left: CW / 4 - width0 / 2,
          top: (CH - height0) / 2,
        });

        // Right three images each get 1/3 of the right half height
        for (let i = 1; i < 4; i++) {
          const img = imgs[i];
          const slotW = CW / 2;
          const slotH = CH / 3;
          const scale = Math.min(slotW / img.w, slotH / img.h);
          const width = img.w * scale;
          const height = img.h * scale;
          result.push({
            src: img.src,
            width,
            height,
            left: CW / 2 + (slotW - width) / 2,
            top: (i - 1) * slotH + (slotH - height) / 2,
          });
        }

        return result;
      },
      calcWhitespace: (positions) => {
        let totalArea = 0;
        positions.forEach(({ width, height }) => {
          totalArea += width * height;
        });
        return CW * CH - totalArea;
      },
    };

    // Layout D: Two images on top row, two images on bottom row
    const layoutD: FourImageLayout = {
      id: "2x2-equal",
      calcPositions: (imgs) => {
        const result: ImageStyle[] = [];

        for (let i = 0; i < 4; i++) {
          const img = imgs[i];
          const row = Math.floor(i / 2);
          const col = i % 2;

          const slotW = CW / 2;
          const slotH = CH / 2;
          const scale = Math.min(slotW / img.w, slotH / img.h);
          const width = img.w * scale;
          const height = img.h * scale;

          result.push({
            src: img.src,
            width,
            height,
            left: col * slotW + (slotW - width) / 2,
            top: row * slotH + (slotH - height) / 2,
          });
        }

        return result;
      },
      calcWhitespace: (positions) => {
        let totalArea = 0;
        positions.forEach(({ width, height }) => {
          totalArea += width * height;
        });
        return CW * CH - totalArea;
      },
    };

    // Define all layouts to test
    const layouts: FourImageLayout[] = [layoutA, layoutB, layoutC, layoutD];

    // Calculate the layouts and their whitespace
    const layoutsWithScores = layouts.map((layout) => {
      const positions = layout.calcPositions(images);
      const whitespace = layout.calcWhitespace(positions);
      return { layout: layout.id, positions, whitespace };
    });

    // Find layout with minimum whitespace
    const bestLayout = layoutsWithScores.reduce(
      (best, current) =>
        current.whitespace < best.whitespace ? current : best,
      layoutsWithScores[0],
    );

    return bestLayout.positions;
  };

  // Render
  return (
    <div>
      <div
        style={{
          position: "relative",
          width: containerWidth,
          height: containerHeight,
          overflow: "hidden",
        }}
      >
        {styles.map(({ src, width, height, top, left }, i) => (
          <img
            key={`image-${i}`}
            src={src}
            alt={`img-${i}`}
            style={{
              position: "absolute",
              width,
              height,
              top,
              left,
            }}
          />
        ))}
      </div>
    </div>
  );
};
